import React, { useEffect } from "react"
import { Card, Badge, CardFooter, Button } from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import useNotificationHub from "hooks/useNotificationHub"
import Notify from "components/Common/notify"
import PaginationComponent from "components/Common/pagination"
import {
  allNotifications,
  NotificationsFetch,
  AddLiveNotification,
  MarkAllNotificationsRead,
} from "redux/slice/notifications/notificationSlice"
import { DateTimeFormatter } from "utilities/dateformater"
import { convertCamelCaseToText } from "utilities/convertText"

const PAGE_SIZE = 10

// Turn a notification payload (string | object | array) into readable text.
const formatMessage = payload => {
  if (payload == null) return "New notification"
  if (typeof payload === "string") return payload
  if (Array.isArray(payload)) return payload.map(formatMessage).join(" ")
  return (
    payload.message ||
    payload.title ||
    payload.body ||
    payload.text ||
    payload.description ||
    JSON.stringify(payload)
  )
}

const formatTime = value => {
  if (!value) return ""
  const d = new Date(value)
  return isNaN(d.getTime()) ? "" : d.toLocaleString()
}

const LatestUpdates = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { notifications, totalPages, currentPage, loading, markAllLoading } =
    useSelector(allNotifications)

  const hasUnread = notifications?.some(
    n => n && typeof n === "object" && !n.isRead,
  )

  const handleMarkAllRead = () => {
    dispatch(MarkAllNotificationsRead())
  }

  // Live notifications via the SignalR notification hub. Each one is prepended
  // to the list (the slice drops the oldest to keep a fixed page-size window).
  const { connected } = useNotificationHub(payload => {
    dispatch(AddLiveNotification(payload))
    Notify("success", formatMessage(payload))
  })

  useEffect(() => {
    dispatch(NotificationsFetch({ pageNumber: 1, pageSize: PAGE_SIZE }))
  }, [dispatch])

  const paginate = pageNumber => {
    dispatch(NotificationsFetch({ pageNumber, pageSize: PAGE_SIZE }))
  }

  return (
    <React.Fragment>
      <style>{`
        .notif-scroll { max-height: 340px; overflow-y: auto; padding-right: 6px; }
        .notif-scroll::-webkit-scrollbar { width: 6px; }
        .notif-scroll::-webkit-scrollbar-thumb { background: #d5d9e0; border-radius: 6px; }
        .notif-scroll::-webkit-scrollbar-thumb:hover { background: #b9bfc9; }
        .notif-item { border-radius: 8px; transition: background-color .15s ease; }
        .notif-item:hover { background-color: #f6f7f9; }
        .notif-item.unread { background-color: #eef4ff; }
      `}</style>
      <Card>
        <div className="card-body" style={{ minHeight: "410px" }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="card-title mb-0">Latest Updates</h4>
            <div className="d-flex align-items-center">
              <Button
                color="link"
                size="sm"
                className="p-0 me-3 text-decoration-none"
                onClick={handleMarkAllRead}
                disabled={markAllLoading || !hasUnread}
                title="Mark all notifications as read"
              >
                {markAllLoading ? "Marking…" : "Mark all as read"}
              </Button>
              <Badge
                className={`rounded-pill d-inline-flex align-items-center ${
                  connected ? "bg-success" : "bg-secondary"
                }`}
                title={
                  connected
                    ? "Live updates connected"
                    : "Live updates disconnected"
                }
              >
                <span
                  className="d-inline-block rounded-circle me-1"
                  style={{
                    width: "8px",
                    height: "8px",
                    backgroundColor: "#fff",
                  }}
                />
                {connected ? "Live" : "Offline"}
              </Badge>
            </div>
          </div>

          <div className="notif-scroll">
            {loading ? (
              <div className="text-center text-muted mt-5">
                <p className="mb-0">Loading notifications…</p>
              </div>
            ) : notifications && notifications.length > 0 ? (
              notifications.map((n, index) => {
                const data =
                  n && typeof n === "object" && !Array.isArray(n) ? n : {}
                const title = data.title || "Notification"
                const message = formatMessage(n)
                const type = data.type
                const time = DateTimeFormatter(data.createdDate)
                const isRead = data.isRead
                const referenceId = data.referenceId

                const handleNotifClick = () => {
                  if (!referenceId) return
                  let target = `/applications/${referenceId}`
                  if (type === "StudentChatMessage") {
                    target += "?focus=chat"
                  } else if (type === "MiscellaneousDocumentUploaded") {
                    target += "?focus=documents"
                  }
                  navigate(target)
                }

                return (
                  <div
                    key={data.id || index}
                    className={`notif-item d-flex align-items-start p-2 mb-1 ${
                      isRead ? "" : "unread"
                    }`}
                    style={referenceId ? { cursor: "pointer" } : undefined}
                    onClick={referenceId ? handleNotifClick : undefined}
                  >
                    {/* <span
                      className={`d-inline-block rounded-circle mt-1 me-2 flex-shrink-0 ${
                        isRead ? "bg-secondary" : "bg-primary"
                      }`}
                      style={{ width: "9px", height: "9px" }}
                    /> */}
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="fw-semibold text-dark text-truncate">
                        {title}
                      </div>
                      <div
                        className="text-muted small"
                        style={{ wordBreak: "break-word" }}
                      >
                        {message}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-1">
                        {type ? (
                          <Badge className="rounded-pill bg-info-subtle text-info fw-normal">
                            {convertCamelCaseToText(type) || type}
                          </Badge>
                        ) : (
                          <span />
                        )}
                        {time && (
                          <small className="text-muted text-nowrap ms-2">
                            {time}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="text-center text-muted mt-5">
                <p className="mb-1">No notifications yet</p>
                <small>
                  You'll see live notifications here as they arrive.
                  {/* {connected
                    ? "You'll see live notifications here as they arrive."
                    : "Waiting for a live connection…"} */}
                </small>
              </div>
            )}
          </div>
        </div>
        <CardFooter className="d-flex justify-content-center">
          <PaginationComponent
            totalPages={totalPages}
            currentPage={currentPage}
            paginate={paginate}
          />
        </CardFooter>
      </Card>
    </React.Fragment>
  )
}

export default LatestUpdates
